import { horaComArtigo } from "../lib/horaNoTexto";
import {
  chaveDoPeriodo,
  comparacaoEmFrase,
  faixaDaMedia,
  notaCurta,
  notaLegivel,
  quandoLegivel,
  rotuloDoPeriodo,
  type EstadoDaLeitura,
  type FaixaDaMedia,
  type Importacao,
  type LinhaDaComparacao,
  type Periodo,
  type SituacaoDaMeta,
} from "../lib/avaliacoes";

/**
 * As peças que as sub-abas do painel das avaliações (000327/000328) usam: o
 * selo da situação da meta, o selo da média, a linha do período, o aviso de
 * leitura que falhou, a frase do banco pronta para a tela, a frase da
 * importação, o sinal da variação e a CHAVE de cada série.
 *
 * Moram fora dos componentes das sub-abas para nenhum importar o outro.
 */

// ── A frase que veio do banco ───────────────────────────────────────────────

/**
 * A frase do banco (ou da chamada), pronta para a tela. Ela vem em minúscula,
 * porque é escrita para caber depois de dois-pontos ("A lista não foi
 * atualizada: a data inicial vem depois da final."), e o ponto final não é
 * contrato de frase nenhuma: sem ele garantido aqui, uma recusa que chegasse
 * sem ponto emendaria com a frase seguinte ("…depois da final O que aparece
 * abaixo…").
 *
 * Num lugar só desde 29/09: havia TRÊS cópias da inicial maiúscula (nos
 * Feedbacks, na meta e na página de uma ferramenta), e o aviso de leitura das
 * outras sub-abas não usava nenhuma — a mesma recusa saía em minúscula na Visão
 * geral e em maiúscula nos Feedbacks.
 */
export function comPonto(texto: string): string {
  const t = texto.trim();
  return !t || /[.!?…]$/.test(t) ? t : `${t}.`;
}

/** Só a inicial maiúscula — para o pedaço de frase que ainda continua ("Por dia, de 31/08…"). */
export function comMaiuscula(texto: string): string {
  return texto ? texto.charAt(0).toLocaleUpperCase("pt-BR") + texto.slice(1) : texto;
}

/** A frase que fica SOZINHA na tela: inicial maiúscula e ponto final. */
export function fraseSolta(texto: string): string {
  return comMaiuscula(comPonto(texto));
}

// ── A importação do app antigo ──────────────────────────────────────────────

/**
 * Até quando a importação do app antigo trouxe avaliações. É um retrato de
 * TODAS as ferramentas, e não do período: as ferramentas da Lovable continuam
 * recebendo avaliação lá, e o número daqui só as vê depois da próxima
 * importação — que é rodada à mão. Sem esta frase, o silêncio de hoje e dos
 * últimos dias se lê como queda ("ninguém avaliou"): em 29/09, as 6.593
 * avaliações do painel tinham vindo todas por importação, e nenhuma do botão do
 * hub.
 *
 * A MESMA frase na Visão geral e na página de uma ferramenta, e por isso ela
 * mora aqui.
 *
 * `importacao` AUSENTE (o banco ainda sem o campo, na janela entre a tela e a
 * migration) devolve nulo, e a tela não escreve nada: ausente é "não sei". Até
 * 29/09 esse caso dizia "Nenhuma avaliação veio do app antigo por importação",
 * que é afirmar zero sobre um número que não chegou.
 */
export function fraseDaImportacao(imp: Importacao | null | undefined): string | null {
  if (!imp) return null;
  if (imp.importadas === 0 || !imp.mais_recente) return "Nenhuma avaliação veio do app antigo por importação.";
  const ate = quandoLegivel(imp.mais_recente);
  const rodou = imp.ultima_importacao ? quandoLegivel(imp.ultima_importacao) : null;
  // «Importação de …», e não «a última rodou em …»: `importada_em` só é
  // carimbado nas linhas que a importação INSERE, então uma rodada sem novidade
  // não deixa rastro, e a frase não pode afirmar que aquela foi a última a rodar
  // (a revisão do banco de 30/09). O parêntese diz de QUAL importação vieram as
  // mais recentes, e nada além disso. Curto de propósito: a frase mora na
  // primeira dobra da Visão geral, e «a última que trouxe avaliação nova rodou
  // em …» a quebrava numa linha a mais (16px, medidos em 30/09). E a hora leva a
  // crase certa: «à 01:12».
  return `A importação do app antigo trouxe as avaliações de todas as ferramentas até ${ate.dia}, ${horaComArtigo(ate.hora)}${
    rodou ? ` (importação de ${rodou.dia}, ${horaComArtigo(rodou.hora)})` : ""
  }; as que chegaram lá depois entram na próxima.`;
}

// ── O sinal da variação ─────────────────────────────────────────────────────

/**
 * ▲ quando subiu, ▼ quando desceu, = quando ficou igual — pelo SINAL do número,
 * e não pelo "melhor": nas baixas, subir é pior e a seta sobe assim mesmo.
 *
 * O igual é "=", e não um traço: até 29/09 a Visão geral desenhava um traço
 * horizontal antes de "0,0%", e ele se lia "−0,0%". A página de uma ferramenta
 * já usava "="; agora as duas usam esta peça.
 *
 * Desenhado em SVG, e não escrito: um caractere entraria no texto da variação,
 * e o texto é o que o leitor de tela lê e o que as provas comparam com o banco.
 * Só para o olho (`aria-hidden`): o número já tem o sinal, e o "melhor", "pior"
 * ou "igual" é dito onde a tela o diz.
 */
export function SinalDaVariacao({ v, className = "shrink-0" }: { v: number; className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 16 16"
      width="0.8em"
      height="0.8em"
      className={className}
      fill="currentColor"
      data-sinal-da-variacao={v > 0 ? "subiu" : v < 0 ? "desceu" : "igual"}
    >
      {v > 0 ? (
        <path d="M8 3 13.5 11.5h-11z" />
      ) : v < 0 ? (
        <path d="M8 13 2.5 4.5h11z" />
      ) : (
        <>
          <rect x="2.5" y="4.5" width="11" height="2.25" rx="1" />
          <rect x="2.5" y="9.25" width="11" height="2.25" rx="1" />
        </>
      )}
    </svg>
  );
}

// ── O período que o banco resolveu ──────────────────────────────────────────

/**
 * A linha que diz de QUE período são os números, e com qual eles são
 * comparados — no alto de cada sub-aba, a partir da RESPOSTA do banco (e não
 * da URL): entre o clique e a resposta, a tela antiga continua de pé, e a
 * linha tem de continuar dizendo a verdade sobre ela.
 */
export function LinhaDoPeriodo({ periodo, atualizando = false }: { periodo: Periodo; atualizando?: boolean }) {
  // Desde a 000334 ela diz também ATÉ QUANDO a comparação vai (as mesmas
  // horas), e por que não há comparação quando o hub ainda não tem avaliação do
  // período — a frase de «Todos» ali diria que falta um período, e o que falta é
  // a importação.
  return (
    <p className="text-sm text-cinza" data-periodo-resolvido={chaveDoPeriodo(periodo)} data-comparacao={periodo.comparacao}>
      <strong className="font-semibold text-tinta">{rotuloDoPeriodo(periodo)}</strong>
      {" · "}
      <span data-comparacao-em-frase>{comparacaoEmFrase(periodo)}</span>
      {atualizando && (
        <span className="ml-2 text-xs" role="status">
          Atualizando…
        </span>
      )}
    </p>
  );
}

/**
 * O que a sub-aba mostra enquanto não há número: o carregador, a recusa do
 * banco (sem permissão) ou a falha — com a frase que veio de lá, pronta para
 * ficar sozinha (`fraseSolta`). Devolve nulo quando há dados, e quem chama
 * desenha a tela.
 *
 * O carregador é o DESTA caixa, e não o `Carregando` da casa: aquele é de tela
 * inteira (`min-h-screen`), e dentro de uma sub-aba ele empurrava o rodapé uma
 * tela para baixo e a página pulava quando os números chegavam. Aqui ele ocupa
 * o lugar de um bloco. O texto continua sendo "Carregando…": é por essa palavra
 * que o instrumento das fotos (`capturas.mjs`) sabe que a tela ainda não está
 * pronta.
 */
export function AvisoDaLeitura<T>({ estado }: { estado: EstadoDaLeitura<T> }) {
  if (estado.fase === "carregando") {
    return (
      <p
        className="surface flex min-h-48 items-center justify-center rounded-cf-lg p-6 text-sm text-cinza"
        role="status"
        data-avaliacoes-carregando
      >
        Carregando…
      </p>
    );
  }
  if (estado.fase === "negado") {
    return (
      <div className="surface rounded-cf-lg p-6" data-avaliacoes-negado>
        <h2 className="font-display text-lg font-bold text-titulo">O banco não devolveu as avaliações</h2>
        <p className="mt-2 text-sm text-cinza">{fraseSolta(estado.detalhe)}</p>
      </div>
    );
  }
  if (estado.fase === "erro") {
    return (
      <div className="surface rounded-cf-lg p-6" role="alert" data-avaliacoes-erro>
        <h2 className="font-display text-lg font-bold text-titulo">Os números não vieram</h2>
        <p className="mt-2 text-sm text-cinza">{fraseSolta(estado.detalhe)}</p>
      </div>
    );
  }
  return null;
}

// ── A média de uma avaliação ────────────────────────────────────────────────

const TOM_DA_FAIXA: Record<FaixaDaMedia, string> = {
  satisfeita: "bg-aviso-fundo text-aviso-tinta",
  intermediaria: "bg-chip text-chip-tinta",
  baixa: "bg-erro-fundo text-erro-tinta",
};
const PALAVRA_DA_FAIXA: Record<FaixaDaMedia, string> = {
  satisfeita: "satisfeita",
  intermediaria: "intermediária",
  baixa: "baixa",
};

/**
 * O selo com a média, pintado pela faixa. A faixa vai também em palavra, para
 * o leitor de tela e para quem não distingue a cor: o número já diz, a palavra
 * confirma. `casas` = 1 para UMA avaliação (ver `notaCurta`), 2 para médias de
 * muitas.
 */
export function SeloDaMedia({ media, casas = 1, grande = false }: { media: number; casas?: 1 | 2; grande?: boolean }) {
  const faixa = faixaDaMedia(media);
  const texto = casas === 1 ? notaCurta(media) : notaLegivel(media);
  return (
    <span
      className={`inline-flex items-center justify-center rounded-full font-semibold tabular-nums ${TOM_DA_FAIXA[faixa]} ${
        grande ? "min-w-16 px-3 py-1 text-lg" : "min-w-11 px-2 py-0.5 text-xs"
      }`}
      data-selo-da-media={faixa}
      title={`média ${texto} — ${PALAVRA_DA_FAIXA[faixa]}`}
    >
      {texto}
      <span className="sr-only"> ({PALAVRA_DA_FAIXA[faixa]})</span>
    </span>
  );
}

// ── A situação da meta ──────────────────────────────────────────────────────

/**
 * O selo da situação: ícone E palavra, nunca só a cor. Os tons são os da casa —
 * `aviso` é o verde de confirmação e `alerta` é o laranja de atenção.
 *
 * A diferença para a meta, no "abaixo", é conta de APRESENTAÇÃO sobre os dois
 * números que a tela já mostra (a média arredondada e a meta); quem decide se
 * está abaixo é o banco.
 */
export function SeloDaSituacao({ linha }: { linha: Pick<LinhaDaComparacao, "situacao_meta" | "media_geral" | "meta"> }) {
  const s: SituacaoDaMeta = linha.situacao_meta;
  const base = "inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-0.5 text-xs font-semibold";
  if (s === "atingida") {
    return (
      <span className={`${base} bg-aviso-fundo text-aviso-tinta`} data-selo-situacao="atingida">
        <svg aria-hidden="true" viewBox="0 0 16 16" width="1em" height="1em" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3.5 8.5l3 3 6-7" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
        Atingida
      </span>
    );
  }
  if (s === "abaixo") {
    const falta = linha.meta !== null && linha.media_geral !== null ? linha.meta - linha.media_geral : null;
    return (
      <span className={`${base} bg-alerta-fundo text-alerta-tinta`} data-selo-situacao="abaixo">
        <svg aria-hidden="true" viewBox="0 0 16 16" width="1em" height="1em" fill="currentColor">
          <path d="M8 12.5 3 5h10z" />
        </svg>
        Abaixo da meta
        {falta !== null && (
          <span className="font-normal tabular-nums">
            (−{falta.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })})
          </span>
        )}
      </span>
    );
  }
  return (
    <span className={`${base} bg-chip text-chip-tinta`} data-selo-situacao={s}>
      {s === "sem_meta" ? "Sem meta" : "Sem avaliação"}
    </span>
  );
}

// ── As séries da comparação ─────────────────────────────────────────────────

/** A posição da série (1 a 4), que é o que decide a cor E a forma. */
export type Posicao = 1 | 2 | 3 | 4;

/** A cor da série: o token que troca com o tema (a tabela está no `index.css`). */
export function corDaSerie(p: Posicao): string {
  return `var(--cf-serie-${p})`;
}

/**
 * A FORMA do marcador de cada série — círculo, quadrado, losango, triângulo.
 * Não é enfeite: no tema escuro a primeira e a quarta cor ficam perto uma da
 * outra para quem não distingue verde e vermelho (a medição está no
 * `index.css`), e a forma é o que as separa sem depender da cor.
 *
 * `cor` troca só a COR (a forma continua a da posição): é a da série única, que
 * pinta com o `acento` (ver `GraficoDeLinhas`).
 */
export function Marcador({
  p,
  x,
  y,
  tamanho = 9,
  anel = true,
  cor,
}: {
  p: Posicao;
  x: number;
  y: number;
  tamanho?: number;
  anel?: boolean;
  cor?: string;
}) {
  const r = tamanho / 2;
  const estilo = {
    fill: cor ?? corDaSerie(p),
    // O anel da cor da superfície: é o que deixa o marcador legível onde ele
    // cruza uma linha ou encosta noutro marcador.
    stroke: anel ? "var(--cf-superficie)" : "none",
    strokeWidth: anel ? 2 : 0,
  } as const;
  if (p === 1) return <circle cx={x} cy={y} r={r} style={estilo} />;
  if (p === 2) return <rect x={x - r * 0.88} y={y - r * 0.88} width={r * 1.76} height={r * 1.76} style={estilo} />;
  if (p === 3) {
    const d = r * 1.18;
    return <path d={`M${x} ${y - d}L${x + d} ${y}L${x} ${y + d}L${x - d} ${y}Z`} style={estilo} />;
  }
  const h = r * 1.15;
  return <path d={`M${x} ${y - h}L${x + h} ${y + h * 0.8}L${x - h} ${y + h * 0.8}Z`} style={estilo} />;
}

/** A chave da série na legenda e na dica: um traço curto da cor, com a forma no meio. `cor` como no `Marcador`. */
export function ChaveDaSerie({ p, cor }: { p: Posicao; cor?: string }) {
  return (
    <svg aria-hidden="true" width="26" height="14" viewBox="0 0 26 14" className="shrink-0" data-chave-da-serie={p}>
      <line x1="1" y1="7" x2="25" y2="7" style={{ stroke: cor ?? corDaSerie(p), strokeWidth: 2, strokeLinecap: "round" }} />
      <Marcador p={p} x={13} y={7} tamanho={8} anel={false} cor={cor} />
    </svg>
  );
}
